import React from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { api, resolveApiAssetUrl } from '../lib/api'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { createCategoryLandingDefaultFormFields, normalizeCategoryLandingLayoutConfig } from '../templates/categoryLanding/registry'
import { isLeadGenFullbleedLayoutKey } from '../templates/categoryLanding/common/leadGenFullbleedKeys'
import { renderCategoryLandingLayout } from '../templates/categoryLanding/layouts/registry'
import LeadPrivacyConsent from '../components/categoryLanding/LeadPrivacyConsent'
import { CheckCircle2 } from 'lucide-react'

function parseJsonArray(raw, fallback = []) {
  if (!raw) return fallback
  if (Array.isArray(raw)) return raw
  try {
    const parsed = JSON.parse(String(raw))
    return Array.isArray(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

function parseJsonObject(raw, fallback = {}) {
  if (!raw) return fallback
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw
  try {
    const parsed = JSON.parse(String(raw))
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

const PERSONAL_INTRO_STAT_DEFAULTS = {
  retentionRate: '92.4%',
  consultCount: '1248',
  responseRate: '98.1%',
  satisfaction: '4.8/5.0',
}

function normalizeIntroStats(raw = []) {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => ({
      key: String(item?.key || '').trim(),
      label: String(item?.label || '').trim(),
      value: String(item?.value || '').trim(),
    }))
    .filter((item) => item.label && item.value)
    .filter((item) => PERSONAL_INTRO_STAT_DEFAULTS[item.key] !== item.value)
}

function SurveyModal({ survey, surveyValues, setSurveyValues, surveyErr, surveySubmitting, submitSurvey, onClose }) {
  if (!survey) return null
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">설문 조사</h3>
        <form onSubmit={submitSurvey} className="space-y-4">
          {survey.fields?.map((f, idx) => {
            const id = String(f?.id || `q_${idx}`)
            const fieldType = String(f?.type || 'text')
            const label = f?.label || id
            const required = Boolean(f?.required)
            if (fieldType === 'textarea') {
              return (
                <div key={id}>
                  <label className="block text-sm text-slate-700 mb-1">{label}{required ? ' *' : ''}</label>
                  <textarea className="w-full rounded border px-3 py-2 min-h-[90px]" value={surveyValues[id] || ''} onChange={(e) => setSurveyValues((p) => ({ ...p, [id]: e.target.value }))} required={required} />
                </div>
              )
            }
            if (fieldType === 'select' && Array.isArray(f?.options)) {
              return (
                <div key={id}>
                  <label className="block text-sm text-slate-700 mb-1">{label}{required ? ' *' : ''}</label>
                  <select className="w-full rounded border px-3 py-2" value={surveyValues[id] || ''} onChange={(e) => setSurveyValues((p) => ({ ...p, [id]: e.target.value }))} required={required}>
                    <option value="">선택하세요</option>
                    {f.options.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                  </select>
                </div>
              )
            }
            return (
              <div key={id}>
                <label className="block text-sm text-slate-700 mb-1">{label}{required ? ' *' : ''}</label>
                <input type="text" className="w-full rounded border px-3 py-2" value={surveyValues[id] || ''} onChange={(e) => setSurveyValues((p) => ({ ...p, [id]: e.target.value }))} required={required} />
              </div>
            )
          })}
          {surveyErr && <div className="text-sm text-red-600">{surveyErr}</div>}
          <div className="flex gap-2">
            <button type="submit" disabled={surveySubmitting} className="flex-1 rounded bg-blue-600 text-white px-4 py-2 font-semibold disabled:opacity-50">
              {surveySubmitting ? '제출 중...' : '제출하기'}
            </button>
            <button type="button" onClick={onClose} className="rounded border px-4 py-2">닫기</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function SurveyDoneModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full text-center">
        <div className="mb-4 flex justify-center">
          <CheckCircle2 className="h-12 w-12 text-emerald-500" aria-hidden="true" />
        </div>
        <h3 className="text-xl font-bold mb-2">설문이 완료되었습니다</h3>
        <p className="text-sm text-slate-600 mb-4">소중한 의견 감사합니다.</p>
        <button onClick={onClose} className="rounded bg-blue-600 text-white px-6 py-2 font-semibold">확인</button>
      </div>
    </div>
  )
}

export default function CategoryLandingPageViewPage() {
  const { code } = useParams()
  const location = useLocation()
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [consents, setConsents] = React.useState(null)
  const [survey, setSurvey] = React.useState(null)
  const [leadId, setLeadId] = React.useState(null)
  const [leadSubmitting, setLeadSubmitting] = React.useState(false)
  const [leadErr, setLeadErr] = React.useState('')
  const [leadOk, setLeadOk] = React.useState(false)
  const [surveyOpen, setSurveyOpen] = React.useState(false)
  const [surveySubmitting, setSurveySubmitting] = React.useState(false)
  const [surveyDone, setSurveyDone] = React.useState(false)
  const [surveyErr, setSurveyErr] = React.useState('')
  const [leadForm, setLeadForm] = React.useState({
    customerName: '',
    customerPhone: '',
    preferredContactTime: '',
    message: '',
  })
  const [consentAccepted, setConsentAccepted] = React.useState(false)
  const [surveyValues, setSurveyValues] = React.useState({})

  React.useEffect(() => {
    let ignore = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const [landingRes, consentRes, surveyRes] = await Promise.all([
          api.get(`/public/category-landing/${encodeURIComponent(code || '')}`, { __silent: true }),
          api.get(`/public/category-landing/${encodeURIComponent(code || '')}/consents`, { __silent: true }),
          api.get(`/public/category-landing/${encodeURIComponent(code || '')}/survey`, { __silent: true }),
        ])
        if (!ignore) {
          setData((landingRes?.data && landingRes.data.data !== undefined) ? landingRes.data.data : landingRes.data)
          setConsents((consentRes?.data && consentRes.data.data !== undefined) ? consentRes.data.data : consentRes.data)
          setSurvey((surveyRes?.data && surveyRes.data.data !== undefined) ? surveyRes.data.data : surveyRes.data)
        }
      } catch (e) {
        const status = e?.response?.status
        if (!ignore) {
          setError(status === 404 ? '페이지를 찾을 수 없습니다.' : (e?.response?.data?.message || e?.message || 'Load failed'))
        }
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => { ignore = true }
  }, [code])

  React.useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search || '')
      const fromQueryLeadId = Number(sp.get('leadId') || 0)
      const shouldOpenSurvey = sp.get('openSurvey') === '1'
      if (fromQueryLeadId > 0) setLeadId(fromQueryLeadId)
      if (shouldOpenSurvey) setSurveyOpen(true)
    } catch {
      // ignore
    }
  }, [location.search])

  if (loading) return <Loading />
  if (error) return <InlineError message={error} />
  if (!data) return <InlineError message="페이지를 찾을 수 없습니다." />

  const contentSections = parseJsonArray(data?.contentSections, [])
  const customConfig = normalizeCategoryLandingLayoutConfig(parseJsonObject(data?.customConfig, {}))
  const formFields = parseJsonArray(data?.formFields, createCategoryLandingDefaultFormFields({ includeMessage: true }))
  const introStatsRaw = Array.isArray(customConfig?.introStats) ? customConfig.introStats : []
  const introStats = data?.category === 'PERSONAL_INTRO' ? normalizeIntroStats(introStatsRaw) : []
  const layoutKey = customConfig?.templateStyle || 'default'
  const fullbleedLeadForm = isLeadGenFullbleedLayoutKey(layoutKey)
  const formBorderColor = customConfig?.formBorderColor || '#3b82f6'
  const formBorderWidth = customConfig?.formBorderWidth || 3
  const recommendedTitle = customConfig?.recommendedTitle || '추천상품'
  const recommendedProducts = Array.isArray(data?.recommendedPages) && data.recommendedPages.length > 0
    ? data.recommendedPages.map((item) => ({ title: item?.title || item?.category || '', href: item?.href || '#' }))
    : Array.isArray(customConfig?.recommendedProducts) ? customConfig.recommendedProducts : []

  const onChangeLead = (name, value) => setLeadForm((prev) => ({ ...prev, [name]: value }))

  const submitLead = async (e) => {
    e.preventDefault()
    setLeadErr('')
    setLeadOk(false)
    if (!consentAccepted) {
      setLeadErr('개인정보 동의가 필요합니다.')
      return
    }
    setLeadSubmitting(true)
    try {
      const payload = {
        customerName: leadForm.customerName,
        customerPhone: leadForm.customerPhone,
        preferredContactTime: leadForm.preferredContactTime,
        message: leadForm.message,
        consentAccepted: true,
        consentVersion: consents?.collectUse?.version || 'CATEGORY_LEAD_COLLECT_USE_V1',
        thirdPartyConsentAccepted: true,
        thirdPartyConsentVersion: consents?.thirdParty?.version || 'CATEGORY_LEAD_THIRD_PARTY_V1',
        website: '',
      }
      const res = await api.post(`/public/category-landing/${encodeURIComponent(code || '')}/leads`, payload, { __silent: true })
      const out = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setLeadId(out?.id || null)
      setLeadOk(true)
    } catch (err) {
      setLeadErr(err?.response?.data?.message || err?.message || '상담 신청에 실패했습니다.')
    } finally {
      setLeadSubmitting(false)
    }
  }

  const submitSurvey = async (e) => {
    e.preventDefault()
    if (!leadId) {
      setSurveyErr('먼저 상담 신청을 완료해 주세요.')
      return
    }
    setSurveyErr('')
    setSurveySubmitting(true)
    try {
      await api.post(`/public/category-landing/${encodeURIComponent(code || '')}/survey/${leadId}`, { responses: surveyValues }, { __silent: true })
      setSurveyDone(true)
    } catch (err) {
      setSurveyErr(err?.response?.data?.message || err?.message || '설문 제출에 실패했습니다.')
    } finally {
      setSurveySubmitting(false)
    }
  }

  const leadFormCard = (
    <div
      className={
        fullbleedLeadForm
          ? 'mx-auto w-full max-w-[300px] rounded-[22px] bg-white p-5 shadow-lg sm:max-w-[320px]'
          : 'w-[380px] max-w-full rounded-[28px] bg-white p-7 shadow-lg'
      }
      style={{ border: `${formBorderWidth}px solid ${formBorderColor}` }}
    >
      <h2
        className={
          fullbleedLeadForm
            ? 'mb-4 text-center text-xl font-extrabold leading-tight text-slate-900 sm:text-2xl'
            : 'mb-5 text-center text-[30px] font-extrabold leading-tight text-slate-900'
        }
      >
        {data.formTitle || '간편 상담 신청'}
      </h2>
      {leadOk && (
        <div
          className={
            fullbleedLeadForm
              ? 'mb-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800'
              : 'mb-4 rounded-xl border border-green-200 bg-green-50 p-4 text-base text-green-800'
          }
        >
          {data?.surveyEnabled && data?.kakaoNotificationEnabled ? (
            <>
              상담 신청이 완료되었습니다.
              <p className="mt-2 text-green-900/90 leading-relaxed">
                잠시 후 등록하신 번호로 카카오 알림톡이 발송됩니다. 하단 링크의 간단한 설문에 참여해 주시면 상담 준비에 큰 도움이 됩니다.
              </p>
            </>
          ) : data?.surveyEnabled ? (
            <>
              상담 신청이 완료되었습니다.
              <p className="mt-2 text-green-900/90 leading-relaxed">담당자가 연락드리며, 필요 시 설문 안내를 드릴 수 있습니다.</p>
            </>
          ) : (
            '상담 신청이 완료되었습니다.'
          )}
        </div>
      )}
      <form className={fullbleedLeadForm ? 'space-y-2.5' : 'space-y-3'} onSubmit={submitLead}>
        {formFields.map((f, idx) => {
          const name = String(f?.name || `field_${idx}`)
          const type = String(f?.type || 'text')
          const label = f?.label || name
          const required = Boolean(f?.required)
          if (type === 'textarea') {
            return (
              <div key={name}>
                <label className={`mb-1.5 block font-medium text-slate-700 ${fullbleedLeadForm ? 'text-sm' : 'text-base'}`}>{label}{required ? ' *' : ''}</label>
                <textarea
                  className={
                    fullbleedLeadForm
                      ? 'min-h-[88px] w-full rounded-lg border px-3 py-2 text-sm'
                      : 'min-h-[110px] w-full rounded-xl border px-4 py-3 text-base'
                  }
                  value={leadForm[name] || ''}
                  onChange={(e) => onChangeLead(name, e.target.value)}
                  required={required}
                />
              </div>
            )
          }
          return (
            <div key={name}>
              <label className={`mb-1.5 block font-medium text-slate-700 ${fullbleedLeadForm ? 'text-sm' : 'text-base'}`}>{label}{required ? ' *' : ''}</label>
              <input
                type={type === 'tel' ? 'tel' : 'text'}
                className={
                  fullbleedLeadForm
                    ? 'w-full rounded-lg border px-3 py-2 text-sm'
                    : 'w-full rounded-xl border px-4 py-3 text-base'
                }
                value={leadForm[name] || ''}
                onChange={(e) => onChangeLead(name, e.target.value)}
                required={required}
                placeholder={type === 'tel' ? '숫자만 입력' : ''}
              />
            </div>
          )
        })}
        <LeadPrivacyConsent
          className={fullbleedLeadForm ? 'mt-3 text-xs' : 'mt-5'}
          collectUse={consents?.collectUse}
          thirdParty={consents?.thirdParty}
          checked={consentAccepted}
          onChange={setConsentAccepted}
          accentColor={formBorderColor}
        />
        {leadErr && <div className="text-sm text-red-600">{leadErr}</div>}
        <button
          type="submit"
          disabled={leadSubmitting}
          className={
            fullbleedLeadForm
              ? 'w-full rounded-xl py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-50'
              : 'w-full rounded-2xl py-4 text-base font-semibold text-white transition-colors disabled:opacity-50'
          }
          style={{ backgroundColor: formBorderColor }}
        >
          {leadSubmitting ? '제출 중...' : (data.formTitle || '상담 신청')}
        </button>
      </form>
    </div>
  )

  const adReviewFooterText = String(data?.adReviewNotice || '').trim()

  const recommendedProductsCard = recommendedProducts.length > 0 ? (
    <div className={fullbleedLeadForm ? 'mx-auto w-[132px] max-w-full' : 'w-[132px]'}>
      <div className="mb-3 text-center text-sm font-bold text-white">
        <span className="inline-flex rounded-full px-3 py-1.5" style={{ backgroundColor: formBorderColor }}>{recommendedTitle}</span>
      </div>
      <div className="rounded-[24px] border border-slate-200 bg-white p-3 shadow-sm">
        <div className="space-y-2">
          {recommendedProducts.map((item, idx) => {
            const href = item?.href || '#'
            return (
              <a
                key={idx}
                href={href}
                className="flex flex-col items-center gap-1 rounded-2xl px-2 py-4 text-center hover:bg-slate-50"
                target={href.startsWith('http') ? '_blank' : undefined}
                rel={href.startsWith('http') ? 'noreferrer' : undefined}
              >
                <div className="text-[15px] font-semibold leading-5 text-slate-900 break-keep">{item?.title || `추천상품 ${idx + 1}`}</div>
              </a>
            )
          })}
        </div>
      </div>
    </div>
  ) : null

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {renderCategoryLandingLayout(layoutKey, {
        mode: 'public',
        heroTitle: data.heroTitle || '',
        heroSubtitle: data.heroSubtitle || '',
        heroImageFileId: data.heroImageFileId || '',
        sections: contentSections,
        formEnabled: data.formEnabled !== false,
        formTitle: data.formTitle || '상담 신청',
        introStats,
        customConfig,
        resolveAssetUrl: resolveApiAssetUrl,
        formCard: data.formEnabled !== false ? leadFormCard : null,
        recommendedProductsCard,
        surveyModal: surveyOpen && survey && !surveyDone ? (
          <SurveyModal
            survey={survey}
            surveyValues={surveyValues}
            setSurveyValues={setSurveyValues}
            surveyErr={surveyErr}
            surveySubmitting={surveySubmitting}
            submitSurvey={submitSurvey}
            onClose={() => setSurveyOpen(false)}
          />
        ) : null,
        surveyDoneModal: surveyDone ? <SurveyDoneModal onClose={() => setSurveyOpen(false)} /> : null,
      })}
      {adReviewFooterText ? (
        <footer className="border-t-2 border-slate-200 bg-slate-50 px-4 py-8 text-slate-700 md:px-6">
          <div className="mx-auto max-w-3xl">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">광고 심의필·준법 표기</div>
            <div className="mt-2 whitespace-pre-line text-xs leading-relaxed text-slate-600 md:text-sm">
              {adReviewFooterText}
            </div>
          </div>
        </footer>
      ) : null}
    </div>
  )
}
